import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Input,
  List,
  ListItem,
  Text,
  useOutsideClick,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/* ---------- CompactTag ---------- */
const CompactTag = ({ label, onRemove }) => (
  <Flex
    as="span"
    align="center"
    flexShrink={0}
    bg="#F0F0F0"
    color="#333"
    borderRadius="full"
    fontSize="14px"
    fontFamily="body"
    fontWeight="600"
    lineHeight="1"
    px={4}
    py="7px"
    mr="6px"
    gap="5px"
    whiteSpace="nowrap"
    _hover={{ bg: '#E4E4E4' }}
    transition="background 0.15s"
  >
    {label}
    <Box
      as="button"
      type="button"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      ml="2px"
      w="14px"
      h="14px"
      borderRadius="full"
      bg="transparent"
      color="#888"
      fontSize="11px"
      lineHeight="1"
      _hover={{ bg: '#CCC', color: '#333' }}
      onClick={(e) => { e.stopPropagation(); onRemove(); }}
      aria-label="Usuń"
    >
      ×
    </Box>
  </Flex>
);

/* ---------- Main component ---------- */
const MultiSearchInput = ({ events, categories, hashtags, onSelectionChange, selectedTags = [] }) => {
  const { t } = useTranslation("default");
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [showFade, setShowFade] = useState(false);

  const ref = useRef();
  const scrollContainerRef = useRef();
  const inputRef = useRef();
  const navigate = useNavigate();

  // Drag-to-scroll state
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false });

  /* Scroll to newest tag when list changes */
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
  }, [selectedTags]);

  /* Show/hide gradient fade based on scroll position */
  const updateFade = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const isOverflowing = el.scrollWidth > el.clientWidth;
    const notAtEnd = el.scrollLeft < el.scrollWidth - el.clientWidth - 2;
    setShowFade(isOverflowing && notAtEnd);
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateFade);
    ro.observe(el);
    el.addEventListener('scroll', updateFade);
    updateFade();
    return () => { ro.disconnect(); el.removeEventListener('scroll', updateFade); };
  }, [selectedTags]);

  useOutsideClick({
    ref: ref,
    handler: () => setIsDropdownVisible(false),
  });

  /* Suggestions logic */
  useEffect(() => {
    if (inputValue.length > 1) {
      const trimmedInput = inputValue.trim().toLowerCase();
      const normalizeHashtag = (text) => text.toLowerCase().replace(/^#+/, '');
      const hasHash = inputValue.startsWith('#');
      const searchInput = hasHash ? normalizeHashtag(trimmedInput) : trimmedInput;

      let currentEventSuggestions = [];
      let currentCategorySuggestions = [];
      let currentHashtagSuggestions = [];

      if (!hasHash) {
        if (selectedTags.length === 0) {
          currentEventSuggestions = (events || [])
            .filter(event => event.title?.trim().toLowerCase().includes(searchInput))
            .map(event => ({ id: event.id, name: event.title, type: 'Wydarzenie' }));

          currentCategorySuggestions = (categories || [])
            .filter(category => category.name?.trim().toLowerCase().includes(searchInput))
            .map(category => ({ ...category, type: 'Kategoria' }));
        } else {
          currentCategorySuggestions = (categories || [])
            .filter(category => category.name?.trim().toLowerCase().includes(searchInput))
            .map(category => ({ ...category, type: 'Kategoria' }));
        }
      }

      currentHashtagSuggestions = (hashtags || [])
        .filter(hashtag => normalizeHashtag(hashtag.name).includes(searchInput))
        .map(hashtag => ({ ...hashtag, type: 'Hashtag' }));

      const combined = [...currentEventSuggestions, ...currentCategorySuggestions, ...currentHashtagSuggestions]
        .filter(suggestion => !selectedTags.some(tag => tag.id === suggestion.id && tag.type === suggestion.type));

      setSuggestions(combined);
      setIsDropdownVisible(combined.length > 0);
    } else {
      setSuggestions([]);
      setIsDropdownVisible(false);
    }
  }, [inputValue, events, categories, hashtags, selectedTags]);

  const handleSelect = (suggestion) => {
    if (suggestion.type === 'Wydarzenie') {
      navigate(`/event/${suggestion.id}`);
    } else if (suggestion.type === 'Kategoria' || suggestion.type === 'Hashtag') {
      if (!selectedTags.some(tag => tag.id === suggestion.id && tag.type === suggestion.type)) {
        onSelectionChange([...selectedTags, suggestion]);
      }
    }
    setInputValue('');
    setSuggestions([]);
    setIsDropdownVisible(false);
  };

  const handleRemove = (tagToRemove) => {
    onSelectionChange(selectedTags.filter(tag => !(tag.id === tagToRemove.id && tag.type === tagToRemove.type)));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && inputValue === '' && selectedTags.length > 0) {
      handleRemove(selectedTags[selectedTags.length - 1]);
    }
  };

  const hasTagsOrInput = selectedTags.length > 0 || inputValue.length > 0;

  return (
    <Box position="relative" ref={ref} w="100%" minH="2.5rem" display="flex" alignItems="center">

      {/* Search icon */}
      <Flex
        position="absolute"
        left="0"
        top="0"
        bottom="0"
        w="2.5rem"
        alignItems="center"
        justifyContent="center"
        pointerEvents="none"
        zIndex={1}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#AAAAAA" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </Flex>

      {/* Inner row: tags + input */}
      <Flex
        align="center"
        w="100%"
        pl="2.5rem"
        pr={1}
        minH="2.5rem"
        onClick={() => inputRef.current?.focus()}
        cursor="text"
        overflow="hidden"
      >
        {/* Tags scroll zone — max 45% of the field's width */}
        {selectedTags.length > 0 && (
          <Box
            position="relative"
            maxW="45%"
            flexShrink={0}
            minW={0}
          >
            <Flex
              ref={scrollContainerRef}
              align="center"
              flexWrap="nowrap"
              overflowX="auto"
              py="4px"
              cursor="grab"
              userSelect="none"
              sx={{
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&:active': { cursor: 'grabbing' },
              }}
              onWheel={(e) => {
                const el = scrollContainerRef.current;
                if (el && e.deltaY !== 0) {
                  e.preventDefault();
                  el.scrollLeft += e.deltaY;
                }
              }}
              onMouseDown={(e) => {
                const el = scrollContainerRef.current;
                if (!el) return;
                dragState.current = { isDown: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft, moved: false };
              }}
              onMouseMove={(e) => {
                const ds = dragState.current;
                if (!ds.isDown) return;
                const el = scrollContainerRef.current;
                if (!el) return;
                const x = e.pageX - el.offsetLeft;
                const walk = (x - ds.startX) * 1.2;
                if (Math.abs(walk) > 3) ds.moved = true;
                el.scrollLeft = ds.scrollLeft - walk;
              }}
              onMouseUp={() => { dragState.current.isDown = false; }}
              onMouseLeave={() => { dragState.current.isDown = false; }}
            >
              {selectedTags.map((tag, index) => (
                <CompactTag
                  key={`${tag.type}-${tag.id}-${index}`}
                  label={tag.type === 'Hashtag' ? `#${tag.name.replace(/^#+/, '')}` : tag.name}
                  onRemove={() => { if (!dragState.current.moved) handleRemove(tag); }}
                />
              ))}
            </Flex>

            {/* Gradient fade-out on right edge */}
            {showFade && (
              <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                w="28px"
                bgGradient="linear(to-r, transparent, white)"
                pointerEvents="none"
                borderRadius="0 full 0 0"
              />
            )}
          </Box>
        )}

        {/* Text input — always present, takes remaining space */}
        <Input
          ref={inputRef}
          variant="outline"
          flex="1"
          minW="60px"
          border="none"
          px={selectedTags.length > 0 ? 4 : 0}
          h="auto"
          py="6px"
          fontSize={{ base: '16px', md: '15px' }}
          fontFamily="'DM Sans', sans-serif"
          fontWeight="400"
          lineHeight="normal"
          color="#787878"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => inputValue.length > 1 && setIsDropdownVisible(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? t('placeholder.search') : ''}
          _placeholder={{ color: '#787878' }}
          sx={{ '--input-font-size': { base: '16px', md: '15px' } }}
          _focus={{ boxShadow: 'none' }}
        />
      </Flex>

      {/* Dropdown suggestions */}
      {isDropdownVisible && suggestions.length > 0 && (
        <Box
          position="absolute"
          top="calc(100% + 6px)"
          left="0"
          right="0"
          bg="white"
          shadow="0 8px 24px rgba(0,0,0,0.12)"
          rounded="16px"
          overflow="hidden"
          zIndex={10}
          border="1px solid #F0F0F0"
        >
          <List spacing={0} py={2}>
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={`${suggestion.type}-${suggestion.id}-${index}`}
                px={4}
                py="10px"
                _hover={{ bg: '#F7F7F7', cursor: 'pointer' }}
                onClick={() => handleSelect(suggestion)}
                transition="background 0.12s"
              >
                <Flex justify="space-between" align="center">
                  <Text fontWeight="600" color="#222" fontFamily="body" fontSize="15px">
                    {suggestion.type === 'Hashtag' ? `#${suggestion.name.replace(/^#+/, '')}` : suggestion.name}
                  </Text>
                  <Text
                    fontSize="11px"
                    fontFamily="body"
                    fontWeight="600"
                    color="#AAA"
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                  >
                    {suggestion.type}
                  </Text>
                </Flex>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default MultiSearchInput;
